import * as React from "react";
import { Link } from "react-router-dom";
import { Banknote, FileText, HandCoins, Plane, Receipt, Wallet } from "lucide-react";

import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/PageHeader";
import {
  bucketApprovals,
  bucketSubmissions,
  DashboardVolumeChart
} from "@/components/DashboardVolumeChart";
import {
  DateRangeValue,
  trailingMonthsRange
} from "@/components/DateRangePicker";
import {
  fetchAdminDashboardVolume,
  fetchAdvancesAwaitingPayment,
  fetchFinanceAdvanceStage,
  fetchFinanceQueue,
  fetchMyAdvanceApprovals,
  fetchMyAdvances,
  fetchMyApprovals,
  fetchMyOtherAdvances,
  fetchMyOtherRequests,
  fetchMyRequests
} from "../api";
import {
  Advance,
  AdvanceApproval,
  Approval,
  AuthUser,
  FinanceQueueRow,
  TravelRequestSummary
} from "../types";
import { isActuallyPending } from "@/lib/approval";

// Each entity's own tint - decorative category colour, not status colour
// (Approved/Pending/Rejected keep the app's existing semantic tokens
// everywhere, including inside these cards - see EntityCard below). Same
// bg-*-500/10 + text-*-600 dark:text-*-400 pattern already used elsewhere
// in this app for a one-off non-brand accent (e.g. the policy-exceeded
// amber callouts in CurrencyConversionCard.tsx/ExpensesStep.tsx) - just
// applied to four hues instead of one, never a literal hex value.
const ENTITY_TINTS = {
  travelRequests: {
    icon: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    card: "border-emerald-500/20 bg-emerald-500/[0.03]"
  },
  travelAdvances: {
    icon: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    card: "border-blue-500/20 bg-blue-500/[0.03]"
  },
  otherRequests: {
    icon: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
    card: "border-violet-500/20 bg-violet-500/[0.03]"
  },
  otherAdvances: {
    icon: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    card: "border-amber-500/20 bg-amber-500/[0.03]"
  }
} as const;

/** One of the 4 big entity cards (Travel Reimbursements / Travel Advances /
 *  Other Reimbursements / Other Advances) - replaces the old flat "My
 *  reimbursements" tile. The whole card is a Link to that entity's own
 *  page (the chevron is the visual cue) - the actual fix for the
 *  "navigating problem" this redesign exists for: a
 *  combined tile could only ever link to ONE page, so an Other
 *  reimbursement's count had nowhere of its own to click through to and
 *  silently landed on /my-requests instead. Approved/Pending/Rejected keep
 *  the SAME status colours as every other status badge/chart in this app
 *  (--chart-approved/--chart-pending/--chart-rejected via
 *  text-primary/text-[hsl(var(--quadra-orange))]/text-destructive) - only
 *  the card's own icon/background tint is decorative-per-entity. */
function EntityCard({
  label,
  description,
  icon: Icon,
  tint,
  total,
  approved,
  pending,
  rejected,
  loading,
  to
}: {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  tint: (typeof ENTITY_TINTS)[keyof typeof ENTITY_TINTS];
  total: number;
  approved: number;
  pending: number;
  rejected: number;
  loading: boolean;
  to: string;
}) {
  return (
    <Link
      to={to}
      className={cn(
        "block h-full min-w-0 rounded-lg border transition-colors hover:brightness-[0.98]",
        tint.card
      )}
    >
      <Card className="h-full min-w-0 overflow-hidden border-none bg-transparent shadow-none">
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 p-2.5 pb-2 sm:p-4 sm:pb-3">
          <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
            <span
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full sm:h-9 sm:w-9",
                tint.icon
              )}
            >
              <Icon aria-hidden="true" className="h-3.5 w-3.5 sm:h-[1.125rem] sm:w-[1.125rem]" />
            </span>
            <div className="min-w-0">
              <CardTitle className="truncate text-xs font-semibold leading-tight sm:text-sm">
                {label}
              </CardTitle>
              {/* Dropped below phone width (2-per-row there leaves too
                 little room for label + description on one line) - the
                 label alone still identifies the card; the description is
                 restored once the card has a full row to itself from sm up. */}
              <p className="hidden truncate text-[0.6875rem] leading-tight text-muted-foreground sm:block">
                {description}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-2 gap-y-2 p-2.5 pt-1 sm:grid-cols-4 sm:gap-2 sm:p-4 sm:pt-1">
          {[
            { label: "Total", value: total, className: "text-foreground" },
            { label: "Approved", value: approved, className: "text-primary" },
            {
              label: "Pending",
              value: pending,
              className: "text-[hsl(var(--quadra-orange))]"
            },
            { label: "Rejected", value: rejected, className: "text-destructive" }
          ].map((stat) => (
            <div key={stat.label} className="min-w-0">
              {loading ? (
                <Skeleton className="h-5 w-8 sm:h-6" />
              ) : (
                <p
                  className={cn(
                    "text-base font-semibold leading-tight tabular-nums sm:text-lg",
                    stat.className
                  )}
                >
                  {stat.value}
                </p>
              )}
              <p className="truncate text-[0.625rem] leading-tight text-muted-foreground sm:text-[0.6875rem]">
                {stat.label}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </Link>
  );
}

/** One of up to 4 small "My Approvals" tiles - a pending count for one
 *  stage+type combination (My Approvals/Approver Queue x requests/
 *  advances), linking straight to that page's matching tab via its own
 *  `to`. Hidden entirely for a non-approver (see the isApprover gate around
 *  the whole row), so this never tells someone with no approvals at all
 *  that they have "0 pending" four times over. */
function ApprovalMiniCard({
  label,
  icon: Icon,
  value,
  loading,
  to
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  loading: boolean;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex min-w-0 items-center justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50 sm:p-4"
    >
      {/* Icon sits right beside the label text itself, not as a separate
         left-hand column spanning the card's full height - that used to
         push the label down and gave the icon more visual weight than the
         content next to it. The count is its own prominent figure on the
         right (a metric-row layout) rather than trailing the label inline
         with a "Pending approval" caption, which repeated the same three
         words across up to 4 cards without adding information the label
         and this section's own title don't already give. */}
      <p className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-muted-foreground sm:text-sm">
        <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-primary sm:h-4 sm:w-4" />
        <span className="min-w-0">{label}</span>
      </p>
      {loading ? (
        <Skeleton className="h-6 w-8 shrink-0" />
      ) : (
        <span className="shrink-0 text-xl font-semibold leading-none tabular-nums sm:text-2xl">
          {value}
        </span>
      )}
    </Link>
  );
}


export default function Dashboard({ user }: { user: AuthUser }) {
  const [requests, setRequests] = React.useState<TravelRequestSummary[]>([]);
  const [approvals, setApprovals] = React.useState<Approval[]>([]);
  const [advances, setAdvances] = React.useState<Advance[]>([]);
  const [advanceApprovals, setAdvanceApprovals] = React.useState<
    AdvanceApproval[]
  >([]);
  const [financeQueue, setFinanceQueue] = React.useState<FinanceQueueRow[]>([]);
  // Advances at THIS user's Finance stage that still need action (Pending
  // routed straight to them, or Approved awaiting disbursement) - a
  // SEPARATE route/count from financeQueue above, which is travel requests'
  // Finance stage only (see requests_api.py's advances_awaiting_payment vs.
  // finance_queue). Without this, a Finance Approver's pending advance
  // disbursements never counted toward "Pending my approval".
  const [financeAdvancesNeedingAction, setFinanceAdvancesNeedingAction] =
    React.useState<Advance[]>([]);
  const [loading, setLoading] = React.useState(true);

  // /api/finance-queue is @require_role("Finance") server-side (unlike
  // /api/my-approvals and /api/my-advance-approvals, both @require_auth and
  // tolerant of "not an approver" by returning []) - calling it for every
  // signed-in user would 403 for the vast majority of them, so it's only
  // fetched when the signed-in user actually holds that role.
  const isFinance = !!user.roles?.includes("Finance");
  const isAdmin = !!user.roles?.includes("Admin");
  const isApprover = isFinance || !!user.isManager;

  React.useEffect(() => {
    Promise.all([
      fetchMyRequests(),
      // Other reimbursements (City travel/Miscellaneous) are a request too,
      // from the employee's own perspective - merged into the same
      // `requests` state below so myTotal/submitted/approved count them
      // alongside travel requests instead of silently excluding them. The
      // approver-side fetches (fetchMyApprovals/fetchFinanceQueue/etc.)
      // already include Other-kind rows with no change needed - those
      // queues were never travel-only (see db/approvals_store.py's
      // list_for_approver, which has no requestKind filter).
      fetchMyOtherRequests(),
      // Unfiltered, not ?status=Pending - the server-side filter can only
      // see approval.status/financeApproval.status, which deliberately
      // stays "Pending" after a resubmit request is sent (so the
      // employee's eventual resubmission routes straight back to the same
      // approver - see db/approvals_store.py). That would otherwise count a
      // request/advance actually waiting on the EMPLOYEE (status
      // ResubmitRequested/Resubmit) as if it were still waiting on this
      // Manager/Finance Approver, inflating managerPending/financePending
      // below. isActuallyPending uses requestStatus to tell them apart,
      // same check MyApprovals.tsx's displayStatus makes per row.
      fetchMyApprovals(),
      fetchMyAdvances(),
      fetchMyOtherAdvances(),
      fetchMyAdvanceApprovals(),
      isFinance ? fetchFinanceQueue() : Promise.resolve([]),
      isFinance ? fetchAdvancesAwaitingPayment() : Promise.resolve([])
    ])
      .then(([r, otherR, a, adv, otherAdv, advA, fq, finAdv]) => {
        setRequests([...r, ...otherR]);
        setApprovals(a.filter(isActuallyPending));
        setAdvances([...adv, ...otherAdv]);
        setAdvanceApprovals(advA.filter(isActuallyPending));
        setFinanceQueue(fq.filter(isActuallyPending));
        setFinanceAdvancesNeedingAction(finAdv);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isFinance]);

  // --- Volume-by-month charts (separate from the stat-card fetches above,
  // which are deliberately Pending-only) - these need every status to stack
  // Approved/Pending/Rejected correctly, so each role's chart fetches its own
  // unfiltered data rather than reusing the stat cards' filtered state.
  const [myRange, setMyRange] = React.useState<DateRangeValue>(() =>
    trailingMonthsRange(6)
  );
  const [approverRange, setApproverRange] = React.useState<DateRangeValue>(() =>
    trailingMonthsRange(6)
  );
  const [adminRange, setAdminRange] = React.useState<DateRangeValue>(() =>
    trailingMonthsRange(6)
  );

  const [approverRequestRows, setApproverRequestRows] = React.useState<
    (Approval | FinanceQueueRow)[]
  >([]);
  const [approverAdvanceRows, setApproverAdvanceRows] = React.useState<
    AdvanceApproval[]
  >([]);
  // The advances at THIS user's Finance stage (awaiting payment / paid /
  // rejected by them) - Finance users only. approverAdvanceRows above is the
  // Manager-side approval record, which never carries a Finance decision.
  const [financeAdvances, setFinanceAdvances] = React.useState<Advance[]>([]);
  const [adminVolume, setAdminVolume] = React.useState<{
    requests: TravelRequestSummary[];
    advances: Advance[];
  }>({
    requests: [],
    advances: []
  });

  // Loading flags for the two chart-only fetches below (the "My submissions"
  // chart reuses the stat cards' own `loading`) - without them the charts
  // briefly showed their "No … for this range" empty state on every load
  // before the data arrived.
  const [approverLoading, setApproverLoading] = React.useState(isApprover);
  const [adminLoading, setAdminLoading] = React.useState(isAdmin);

  React.useEffect(() => {
    if (!isApprover) return;
    setApproverLoading(true);
    Promise.all([
      fetchMyApprovals(),
      isFinance ? fetchFinanceQueue() : Promise.resolve([]),
      fetchMyAdvanceApprovals(),
      isFinance ? fetchFinanceAdvanceStage() : Promise.resolve([])
    ])
      .then(([a, fq, advA, finAdv]) => {
        setApproverRequestRows([...a, ...fq]);
        setApproverAdvanceRows(advA);
        setFinanceAdvances(finAdv);
      })
      .catch(() => {})
      .finally(() => setApproverLoading(false));
  }, [isApprover, isFinance]);

  React.useEffect(() => {
    if (!isAdmin) return;
    setAdminLoading(true);
    fetchAdminDashboardVolume()
      .then(setAdminVolume)
      .catch(() => {})
      .finally(() => setAdminLoading(false));
  }, [isAdmin]);

  const myChartData = React.useMemo(
    () => bucketSubmissions(requests, advances, myRange),
    [requests, advances, myRange]
  );
  const approverChartData = React.useMemo(
    () =>
      bucketApprovals(
        approverRequestRows,
        approverAdvanceRows,
        approverRange,
        financeAdvances
      ),
    [approverRequestRows, approverAdvanceRows, approverRange, financeAdvances]
  );
  const adminChartData = React.useMemo(
    () =>
      bucketSubmissions(adminVolume.requests, adminVolume.advances, adminRange),
    [adminVolume, adminRange]
  );

  // One card, switchable by a dropdown, instead of up to three stacked cards
  // - each view keeps its own independent range/data so switching back to a
  // view doesn't lose the picker selection made on it earlier.
  type VolumeView = {
    id: string;
    label: string;
    description: string;
    data: ReturnType<typeof bucketSubmissions>;
    loading: boolean;
    range: DateRangeValue;
    onRangeChange: (range: DateRangeValue) => void;
  };
  const views: VolumeView[] = [
    ...(isAdmin
      ? [
          {
            id: "admin",
            label: "Org-wide volume",
            description: "Every travel request and advance raised, by month",
            data: adminChartData,
            loading: adminLoading,
            range: adminRange,
            onRangeChange: setAdminRange
          }
        ]
      : []),
    ...(isApprover
      ? [
          {
            id: "approver",
            label: "My approval queue",
            description: "Items routed to you for approval, by month",
            data: approverChartData,
            loading: approverLoading,
            range: approverRange,
            onRangeChange: setApproverRange
          }
        ]
      : []),
    {
      id: "mine",
      label: "My submissions",
      description: "Your own travel requests and advances, by month",
      data: myChartData,
      loading,
      range: myRange,
      onRangeChange: setMyRange
    }
  ];
  const [activeView, setActiveView] = React.useState(views[0].id);
  const selected = views.find((v) => v.id === activeView) ?? views[0];

  // The 4 EntityCards replace the old flat "My reimbursements" tile -
  // requestKind tells travel apart from Other (absent/"travel" vs "other",
  // same check requestTypeLabel in types.ts makes); requests/advances
  // already merge both kinds (see the fetch above), so no new fetch is
  // needed here, just splitting what's already loaded. Each card's own
  // Approved/Pending/Rejected mirrors the single-tile logic this replaces:
  // an advance only reads "Approved" once truly Paid (its own "Approved"
  // status still means awaiting Finance's payment decision, so that counts
  // as Pending here, not Approved - same reasoning the old `submitted`/
  // `approved` calc used).
  const travelRequests = requests.filter((r) => r.requestKind !== "other");
  const otherRequests = requests.filter((r) => r.requestKind === "other");
  const travelAdvances = advances.filter((a) => a.requestKind !== "other");
  const otherAdvances = advances.filter((a) => a.requestKind === "other");

  const requestBreakdown = (rows: TravelRequestSummary[]) => ({
    total: rows.length,
    approved: rows.filter((r) => r.status === "Approved" || r.status === "Completed").length,
    pending: rows.filter(
      (r) => r.status === "Submitted" || r.status === "PendingFinance" || r.status === "ResubmitRequested"
    ).length,
    rejected: rows.filter((r) => r.status === "Rejected").length
  });
  const advanceBreakdown = (rows: Advance[]) => ({
    total: rows.length,
    approved: rows.filter((a) => a.status === "Paid").length,
    pending: rows.filter(
      (a) => a.status === "Pending" || a.status === "Approved" || a.status === "Resubmit"
    ).length,
    rejected: rows.filter((a) => a.status === "Rejected").length
  });
  // MyRequests/OtherReimbursements filter on one status per tab, so each
  // card's own link lands on whichever stage actually holds something for
  // THAT entity (Manager stage first, as the earlier one) - status/
  // advanceStatus are independent params (see MyRequests.tsx's
  // VALID_*_PARAMS comment).
  const requestLink = (rows: TravelRequestSummary[], base: string) => {
    const manager = rows.filter((r) => r.status === "Submitted").length;
    const finance = rows.filter((r) => r.status === "PendingFinance").length;
    const status = manager === 0 && finance > 0 ? "PendingFinance" : manager > 0 ? "Submitted" : "All";
    return `${base}?view=requests&status=${status}`;
  };
  const advanceLink = (rows: Advance[], base: string) => {
    const manager = rows.filter((a) => a.status === "Pending").length;
    const finance = rows.filter((a) => a.status === "Approved").length;
    const status = manager === 0 && finance > 0 ? "Approved" : manager > 0 ? "Pending" : "All";
    return `${base}?view=advances&advanceStatus=${status}`;
  };

  // First name only ("Ranjith" from "Ranjith Bharathi R") - the full legal/
  // display name is still shown elsewhere (the sidebar footer, approval
  // rows); this greeting is the one place a shorter, more personal form
  // reads better. Falls back to the whole name for a single-word name.
  const firstName = user.name.trim().split(/\s+/)[0] || user.name;

  return (
    <PageContainer>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Welcome back, {firstName}!
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Here's an overview of your reimbursement requests and approvals.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {(() => {
          const r = requestBreakdown(travelRequests);
          return (
            <EntityCard
              label="Travel Reimbursements"
              description="Business travel expense claims"
              icon={Plane}
              tint={ENTITY_TINTS.travelRequests}
              total={r.total}
              approved={r.approved}
              pending={r.pending}
              rejected={r.rejected}
              loading={loading}
              to={requestLink(travelRequests, "/my-requests")}
            />
          );
        })()}
        {(() => {
          const a = advanceBreakdown(travelAdvances);
          return (
            <EntityCard
              label="Travel Advances"
              description="Advance requests for upcoming travel"
              icon={Wallet}
              tint={ENTITY_TINTS.travelAdvances}
              total={a.total}
              approved={a.approved}
              pending={a.pending}
              rejected={a.rejected}
              loading={loading}
              to={advanceLink(travelAdvances, "/my-requests")}
            />
          );
        })()}
        {(() => {
          const r = requestBreakdown(otherRequests);
          return (
            <EntityCard
              label="Other Reimbursements"
              description="Non-travel expense reimbursements"
              icon={Receipt}
              tint={ENTITY_TINTS.otherRequests}
              total={r.total}
              approved={r.approved}
              pending={r.pending}
              rejected={r.rejected}
              loading={loading}
              to={requestLink(otherRequests, "/other-reimbursements")}
            />
          );
        })()}
        {(() => {
          const a = advanceBreakdown(otherAdvances);
          return (
            <EntityCard
              label="Other Advances"
              description="Advance requests for non-travel expenses"
              icon={HandCoins}
              tint={ENTITY_TINTS.otherAdvances}
              total={a.total}
              approved={a.approved}
              pending={a.pending}
              rejected={a.rejected}
              loading={loading}
              to={advanceLink(otherAdvances, "/other-reimbursements")}
            />
          );
        })()}
      </div>

      {/* Hidden entirely for a non-approver - no reporting-manager
         relationship and no Finance role means both cards below would
         always be hidden anyway (see their own isManager/isFinance gates),
         leaving an empty card with nothing in it. */}
      {isApprover && (
        <Card>
          <CardHeader className="space-y-0 pb-3">
            <CardTitle className="text-base font-semibold">
              My Approvals
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Requests awaiting your approval
            </p>
          </CardHeader>
          {/* Split by STAGE (Manager vs Finance) x TYPE (requests vs
             advances) - My Approvals and Approver Queue are each already
             one table covering both Travel and Other together (no
             per-entity tabs there the way the 4 EntityCards above split
             them), so a per-entity split here just duplicated navigation
             those pages already offer; Requests vs Advances is the split
             those pages actually use as their own tabs. Each stage's pair
             is shown only to someone who can act at it - isManager for the
             Manager-stage pair (a Finance-only user who manages nobody has
             nothing to see in My Approvals), isFinance for the
             Finance-stage pair (@require_role("Finance") server-side, same
             gate the financeQueue/financeAdvancesNeedingAction fetches
             above use) - so this is 2 boxes for a user with only one role,
             4 for someone with both. */}
          <CardContent
            className={cn(
              "grid grid-cols-2 gap-3 pt-0",
              user.isManager && isFinance && "lg:grid-cols-4"
            )}
          >
            {user.isManager && (
              <>
                <ApprovalMiniCard
                  label="My Approvals - Requests"
                  icon={FileText}
                  value={approvals.length}
                  loading={approverLoading}
                  to="/my-approvals?view=requests"
                />
                <ApprovalMiniCard
                  label="My Approvals - Advances"
                  icon={Wallet}
                  value={advanceApprovals.length}
                  loading={approverLoading}
                  to="/my-approvals?view=advances"
                />
              </>
            )}
            {isFinance && (
              <>
                <ApprovalMiniCard
                  label="Approver Queue - Requests"
                  icon={FileText}
                  value={financeQueue.length}
                  loading={approverLoading}
                  to="/approver-queue?view=requests"
                />
                <ApprovalMiniCard
                  label="Approver Queue - Advances"
                  icon={Banknote}
                  value={financeAdvancesNeedingAction.length}
                  loading={approverLoading}
                  to="/approver-queue?view=advances"
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <DashboardVolumeChart
        title={selected.label}
        description={selected.description}
        data={selected.data}
        isLoading={selected.loading}
        range={selected.range}
        onRangeChange={selected.onRangeChange}
        viewSelector={
          <Select value={activeView} onValueChange={setActiveView}>
            <SelectTrigger className="w-full sm:w-[11.25rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {views.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </PageContainer>
  );
}
